/**
 * Reverse index: camera slug → the comparison pages that feature it.
 *
 * The 200 /compare/sku/* pages ship in sitemap-main.xml but, until this index
 * existed, nothing on the site linked to them: measured 2026-08-26, 201 of 222
 * pages under /compare/ were unreachable from the homepage link graph, so their
 * only discovery path was the sitemap. Sitemap-only discovery is the weakest
 * signal Google accepts, and 105 of the 200 were nonetheless ranking, which
 * suggests the rest are held back by reachability rather than by content.
 *
 * Built once, lazily, the same way the sibling pools in cameraSlug.ts are: the
 * SSG asks for this on every one of the ~21.8k camera pages it renders.
 */
import { SKU_COMPARISON_PAIRS, type SkuPair } from '@/data/skuComparisonPairs.generated';

let _byCamera: Map<string, SkuPair[]> | null = null;

function getIndex(): Map<string, SkuPair[]> {
  if (_byCamera) return _byCamera;
  const idx = new Map<string, SkuPair[]>();
  for (const pair of SKU_COMPARISON_PAIRS) {
    for (const side of [pair.a, pair.b] as const) {
      let list = idx.get(side);
      if (!list) { list = []; idx.set(side, list); }
      list.push(pair);
    }
  }
  _byCamera = idx;
  return _byCamera;
}

/**
 * Comparison pages featuring `slug`, with the opposite camera's slug resolved
 * so the caller can label the link without a second lookup.
 *
 * Uncapped by default, deliberately. Capping the rail looks tidier but silently
 * strands the pages this index exists to reach: at a cap of 4, thirty of the
 * 200 comparison pages still ended up with zero inbound links, and only an
 * uncapped rail leaves none behind. The input is bounded — a camera appears in
 * at most 11 pairs, and only 21 cameras exceed three — so "uncapped" here means
 * a worst case of 11 cards on a single page, not an unbounded list.
 *
 * Coverage by cap, measured 2026-08-26 over the 200 pairs:
 *   cap  2 -> 56 pages orphaned   cap  5 -> 20 orphaned
 *   cap  3 -> 42 pages orphaned   cap  6 -> 12 orphaned
 *   cap  4 -> 30 pages orphaned   cap  8 ->  2 orphaned   uncapped -> 0
 */
export function getComparisonsForCamera(
  slug: string,
  max = Number.POSITIVE_INFINITY,
): Array<{ slug: string; otherSlug: string }> {
  return getIndex().get(slug)?.slice(0, max).map(p => ({
    slug: p.slug,
    otherSlug: p.a === slug ? p.b : p.a,
  })) ?? [];
}
